import "./draw.less"
import { classNames, hasText } from "utils/helpers"
import PresentationQuestion from "../components/media/presentation-question"
import {
  DEFAULT_DRAW_IMAGE,
  DrawingSeries,
  PlayDrawingState,
  Point,
} from "../play-model"
import {
  MouseEvent as ReactMouseEvent,
  SyntheticEvent,
  TouchEvent,
  useEffect,
  useRef,
} from "react"
import usePartialState from "utils/use-partial-state"
import { usePlayState } from "play/hooks/use-play-state"
import { useMessages } from "play/language/language"

const TWO_PI = 2 * Math.PI

export const DEFAULT_DRAW_RADIUS = 3

type Color = {
  backgroundColor: string
  borderColor?: string
}

const COLORS: Color[] = [
  {
    backgroundColor: "#000000",
  },
  {
    backgroundColor: "#1d0fff",
  },
  {
    backgroundColor: "#34b800",
  },
  {
    backgroundColor: "#ffb500",
  },
  {
    backgroundColor: "#ff0000",
  },
  {
    backgroundColor: "#ffffff",
    borderColor: "#f4f4f4",
  },
]

type DrawProps = {
  enabled: boolean
  questionImage?: string
  questionText?: string
  drawingSeries: PlayDrawingState["drawingSeries"]

  onAddSeries(payload: DrawingSeries): void
  onUndo(): void
  onAddPoint(point: Point): void
}

export default function Draw() {
  const { drawing, screen, isReview, modifyState } = usePlayState()

  function addSeries(payload: DrawingSeries) {
    modifyState((state) => {
      state.drawing.drawingSeries.push(payload)
    })
  }

  function addPoint(payload: Point) {
    modifyState((state) => {
      if (state.drawing.drawingSeries.length <= 0) {
        return
      }

      const current =
        state.drawing.drawingSeries[state.drawing.drawingSeries.length - 1]
      current.points.push(payload)
    })
  }

  function undo() {
    modifyState((state) => {
      if (state.drawing.drawingSeries.length <= 0) {
        return
      }
      state.drawing.drawingSeries.pop()
    })
  }

  return (
    <DrawScreenView
      drawingSeries={drawing.drawingSeries}
      questionImage={screen?.questionImage}
      questionText={screen?.questionText}
      enabled={!isReview}
      onAddSeries={addSeries}
      onAddPoint={addPoint}
      onUndo={undo}
    />
  )
}

type Tracking = "mouse" | "touch" | "none"

type State = {
  selectedColorIndex: number
  tracking: Tracking
  isMouseInside: boolean
  isMouseDown: boolean
  isTouch: boolean
  colors: string[]
  failedResizeCount: number
  x: number
  y: number
  w: number
  h: number
  maxWidth: number
  maxHeight: number
  naturalWidth: number
  naturalHeight: number
}

export function DrawScreenView(props: DrawProps) {
  const msg = useMessages()
  const svgElement = useRef<SVGSVGElement>(null)
  const containerElement = useRef<HTMLDivElement>(null)
  const colorsContainerRef = useRef<HTMLDivElement>(null)
  const imageContainerElement = useRef<HTMLDivElement>(null)
  const imgElement = useRef<HTMLImageElement>(null)
  const [state, setState, setPartialState] = usePartialState<State>({
    selectedColorIndex: 0,
    isMouseDown: false,
    isMouseInside: false,
    isTouch: false,
    tracking: "none",
    colors: COLORS.map((x) => x.backgroundColor),
    failedResizeCount: 0,
    x: 0,
    y: 0,
    w: 0,
    h: 0,
    naturalWidth: 0,
    naturalHeight: 0,
    maxWidth: 0,
    maxHeight: 0,
  })

  const {
    questionImage,
    questionText,
    enabled,
    onAddPoint,
    onUndo,
    onAddSeries,
    drawingSeries,
  } = props
  const { selectedColorIndex, colors, x, y, w, h } = state

  let imgSrc: string
  if (hasText(questionImage)) {
    imgSrc = questionImage!
  } else {
    imgSrc = DEFAULT_DRAW_IMAGE
  }

  // console.log(DEFAULT_DRAW_IMAGE)

  function updateCanvasSize(img: HTMLImageElement | null) {
    // console.log('updateCanvasSize')

    const drawContainer = containerElement.current
    if (!drawContainer) {
      console.warn("no drawing container")
      return
    }
    if (!img) {
      console.warn("no img element")
      return
    }

    setState((prev) => {
      const next = { ...prev }

      const maxRt = drawContainer.getBoundingClientRect()

      const nw = img.naturalWidth
      const nh = img.naturalHeight
      const maxW = maxRt.width
      const maxH = maxRt.height

      next.naturalWidth = nw
      next.naturalHeight = nh
      next.maxWidth = maxW
      next.maxHeight = maxH

      if (maxW >= maxH) {
        // landscape container
        // limiting factor is height

        next.h = maxH
        next.w = (nw / nh) * next.h
        while (next.w > maxW) {
          next.h *= 0.99
          next.w = (nw / nh) * next.h
        }
      } else {
        // portrait container
        // limiting factor is width

        next.w = maxW
        next.h = (nh / nw) * next.w
        while (next.h > maxH) {
          next.w *= 0.99
          next.h = (nh / nw) * next.w
        }
      }

      next.x = (next.maxWidth - next.w) / 2
      next.y = (next.maxHeight - next.h) / 2

      return next
    })
  }

  function handleImageLoaded(e: SyntheticEvent<HTMLImageElement>) {
    updateCanvasSize(e.currentTarget)
  }

  useEffect(() => {
    function handleWindowResize() {
      // console.log('window resize!!')
      updateCanvasSize(imgElement.current)
    }

    document.addEventListener("mouseup", handleDocumentMouseUp)
    window.addEventListener("resize", handleWindowResize)
    return () => {
      window.removeEventListener("resize", handleWindowResize)
      document.removeEventListener("mouseup", handleDocumentMouseUp)
    }
  }, [imgSrc])

  function handleColorSelected(colorIndex: number) {
    if (!enabled) return

    setPartialState({ selectedColorIndex: colorIndex })
  }

  function handleTouchStarted(e: TouchEvent<SVGElement>) {
    // e.preventDefault();

    if (!svgElement.current || !enabled) {
      return
    }

    const containerRt = e.currentTarget.getBoundingClientRect()

    const countChanges = e.changedTouches.length
    for (let changeIndex = 0; changeIndex < countChanges; changeIndex++) {
      const change = e.changedTouches.item(changeIndex)

      if (
        change.clientX >= containerRt.left &&
        change.clientX < containerRt.right &&
        change.clientY >= containerRt.top &&
        change.clientY < containerRt.bottom
      ) {
        let x = (change.clientX - containerRt.left) / w
        let y = (change.clientY - containerRt.top) / h

        onAddSeries({
          color: state.colors[state.selectedColorIndex],
          points: [{ x, y }],
          radius: DEFAULT_DRAW_RADIUS,
        })
      }
    }
  }

  function handleTouchMove(e: TouchEvent<SVGElement>) {
    // e.preventDefault();
    if (!svgElement.current || !enabled) {
      return
    }

    const containerRt = e.currentTarget.getBoundingClientRect()

    const countChanges = e.changedTouches.length
    for (let changeIndex = 0; changeIndex < countChanges; changeIndex++) {
      const change = e.changedTouches.item(changeIndex)

      if (
        change.clientX >= containerRt.left &&
        change.clientX < containerRt.right &&
        change.clientY >= containerRt.top &&
        change.clientY < containerRt.bottom
      ) {
        let x = (change.clientX - containerRt.left) / w
        let y = (change.clientY - containerRt.top) / h

        onAddPoint({ x, y })
      }
    }
  }

  function handleTouchEnded(e: TouchEvent<SVGElement>) {
    if (!enabled) return

    // e.preventDefault();
    setPartialState({ tracking: "none" })
  }

  function handleMouseDown(e: ReactMouseEvent) {
    if (!enabled) return

    const clientX = e.clientX
    const clientY = e.clientY
    setState((prevState) => {
      const container = svgElement.current
      if (container && prevState.isMouseInside) {
        const containerRt = container.getBoundingClientRect()

        let x = (clientX - containerRt.left) / w
        let y = (clientY - containerRt.top) / h

        onAddSeries({
          color: prevState.colors[prevState.selectedColorIndex],
          points: [{ x, y }],
          radius: 3,
        })
      }
      return {
        ...prevState,
        isMouseDown: true,
      }
    })
  }

  function handleMouseEnter() {
    if (!enabled) return

    setPartialState({
      isMouseInside: true,
    })
  }

  function handleMouseLeave() {
    if (!enabled) return

    setPartialState({
      isMouseInside: false,
    })
  }

  function handleMouseMove(e: ReactMouseEvent) {
    if (!enabled) return

    if (state.isMouseDown && state.isMouseInside && svgElement.current) {
      const containerRt = svgElement.current.getBoundingClientRect()

      let x = (e.clientX - containerRt.left) / w
      let y = (e.clientY - containerRt.top) / h

      onAddPoint({ x, y })
    }
  }

  function handleDocumentMouseUp(e: MouseEvent) {
    if (!enabled) return

    setPartialState({ isMouseDown: false })
  }

  function handleMouseUp() {
    if (!enabled) return

    setPartialState({ isMouseDown: false })
  }

  function handleUndo() {
    if (!enabled) return

    onUndo()
  }

  return (
    <div
      className={classNames("draw-screen play-screen", {
        "has-question": hasText(questionText),
      })}
    >
      <PresentationQuestion question={questionText} />
      <div className="draw-container" ref={containerElement}>
        <img
          src={imgSrc}
          ref={imgElement}
          onLoad={handleImageLoaded}
          alt={msg.screenDrawImageAlt}
          tabIndex={-1}
          draggable={false}
          style={{ width: w, height: h, top: y, left: x }}
        />
        <svg
          ref={svgElement}
          width={w}
          height={h}
          style={{ width: w, height: h, top: y, left: x }}
          // draggable={false}
          onTouchStart={handleTouchStarted}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnded}
          onMouseEnter={handleMouseEnter}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {drawingSeries.map((command, commandIndex) => {
            if (command.points.length === 0) return null
            if (command.points.length === 1) {
              const cx = Math.round(command.points[0].x * w)
              const cy = Math.round(command.points[0].y * h)

              return (
                <circle
                  key={commandIndex}
                  r={1}
                  cx={cx}
                  cy={cy}
                  strokeWidth={command.radius}
                  stroke={command.color}
                />
              )
            }

            let d = ""
            const len = command.points.length - 1
            for (let i = 1; i < len; i++) {
              const prev = command.points[i - 1]
              const pt = command.points[i]

              const mx = Math.round(prev.x * w)
              const my = Math.round(prev.y * h)

              const lx = Math.round(pt.x * w)
              const ly = Math.round(pt.y * h)

              d += ` M${mx} ${my} L${lx} ${ly}`
            }

            return (
              <path
                key={commandIndex}
                d={d}
                strokeWidth={command.radius}
                stroke={command.color}
              />
            )
          })}
        </svg>

        {/*/>*/}
      </div>

      <div className="colors" ref={colorsContainerRef}>
        {colors.map((color, colorIndex) => {
          return (
            <button
              key={colorIndex}
              className={classNames("color", {
                selected: colorIndex === selectedColorIndex,
              })}
              onClick={() => handleColorSelected(colorIndex)}
              style={COLORS[colorIndex]}
            />
          )
        })}
        <button className="undo" onClick={handleUndo} />
      </div>
    </div>
  )
}
